import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import useIsLoading from "../../../hooks/useIsLoading";
import useMessage from "../../../hooks/useMessage";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { fetchExpertUsers } from "../../user/expertUsersSlice";
import { FormContainer } from "../customerManagement/styles";
import {
  fetchCustomerOrgById,
  updateCustomerOrg,
  updateCustomerCompanyClassification,
} from "../customerOrganizationSlice";
import {
  createNewCustomerUser,
  fetchCustomerUsersByCompanyId,
  selectCustomerUsersByIds,
  selectFormattedCustomerUserByIdOrNull,
  updateCustomerAppUser,
} from "../customerUsersSlice";
import { ActionButtonsContainer } from "../styles";
import {
  CustomerUser,
  DeliveryMethod,
  InvoiceSettings,
} from "../types/queries";
import CompanyBasicInfo from "./CompanyBasicInfo";
import CustomerContacts from "./CustomerContacts";
import EezyContactPerson from "./EezyContactPerson";
import InvoiceInfo from "./InvoiceInfo";
import InvoiceReferencesSection from "./InvoiceReferencesSection";
import { isByEInvoice, isByEmail } from "./helpers";
import { InvoiceDelivery } from "../types/commands";
import { CustomerOrganizationForm, InvoiceSettingsForm } from "./types";

const CompanyInfoTabContent = () => {
  const { customerCompanyId } = useParams();
  const [searchParams] = useSearchParams();
  const customerOrgId = searchParams.get("org");
  const [isDataReset, setIsDataReset] = useState(false);

  const { fieldOfWork } = useAppSelector((state) => state.localized);

  const { isAdmin } = useContext(AccessRightContext);
  const { sendMessage } = useMessage();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  useEffect(() => {
    if (customerCompanyId) {
      dispatch(fetchCustomerUsersByCompanyId({ customerCompanyId }));
    }
  }, [dispatch, customerCompanyId]);

  useEffect(() => {
    if (customerOrgId && customerOrgId !== "null") {
      dispatch(fetchCustomerOrgById({ id: customerOrgId }));
    }
  }, [customerOrgId, dispatch]);

  const customerOrganizationDetails = useAppSelector(
    (state) => state.customerOrganization,
  );

  const isLoading = useIsLoading([
    "expertUsers",
    "customerUsers",
    "customerOrganization",
  ]);

  const customerContacts = useAppSelector(
    selectCustomerUsersByIds(customerOrganizationDetails.customerContacts),
  );

  const invoiceContactPerson = useAppSelector(
    selectFormattedCustomerUserByIdOrNull(
      customerOrganizationDetails?.invoiceContactPerson?.id,
    ),
  );

  const industry =
    fieldOfWork && customerOrganizationDetails?.industry
      ? fieldOfWork[customerOrganizationDetails?.industry]
      : "";

  const eezyContactPerson = customerOrganizationDetails?.salesPersonId
    ? {
        salesPersonId: customerOrganizationDetails?.salesPersonId,
        salesPersonName: customerOrganizationDetails?.salesPersonName,
      }
    : null;

  const invoiceReferences = customerOrganizationDetails?.invoiceReferences
    ? {
        orderIdentifier:
          customerOrganizationDetails?.invoiceReferences?.orderIdentifier || "",
        agreementIdentifier:
          customerOrganizationDetails?.invoiceReferences?.agreementIdentifier ||
          "",
        sellerReferenceIdentifier:
          customerOrganizationDetails?.invoiceReferences
            ?.sellerReferenceIdentifier || "",
        buyerReferenceIdentifier:
          customerOrganizationDetails?.invoiceReferences
            ?.buyerReferenceIdentifier || "",
      }
    : null;

  const getInvoiceDetails = (
    invoiceSettings: InvoiceSettings,
  ): InvoiceSettingsForm => {
    if (isByEInvoice(invoiceSettings)) {
      return {
        deliveryMethod: DeliveryMethod.BY_E_INVOICE,
        address: invoiceSettings.deliveryAddress.address || "",
        operatorId: invoiceSettings.deliveryAddress.operatorId || "",
      };
    }

    if (isByEmail(invoiceSettings)) {
      return {
        deliveryMethod: DeliveryMethod.BY_EMAIL,
        emailAddress: invoiceSettings.deliveryAddress.address || "",
      };
    }

    return {
      deliveryMethod: DeliveryMethod.BY_OPERATOR,
    };
  };

  const invoiceSettings: InvoiceSettingsForm = getInvoiceDetails(
    customerOrganizationDetails.invoiceSettings,
  );

  const defaultValues = {
    name: customerOrganizationDetails?.name || "",
    street: customerOrganizationDetails?.billingAddress?.street || "",
    city: customerOrganizationDetails?.billingAddress?.city || "",
    zipCodeAndCity: `${
      customerOrganizationDetails?.billingAddress?.zip || ""
    } ${customerOrganizationDetails?.billingAddress?.city || ""}`,
    eezyContactPerson: eezyContactPerson,
    businessId: customerOrganizationDetails?.businessId || "",
    customerNumber: customerOrganizationDetails?.customerNumber || "",
    salesPermission: {
      verificationDate:
        customerOrganizationDetails?.salesPermission?.verificationDate,
      riskLevel: customerOrganizationDetails?.salesPermission?.riskLevel,
    },
    isNationwide: customerOrganizationDetails?.isNationwide || false,
    invoiceSettings,
    customerContacts,
    industry,
    invoiceReferences: invoiceReferences,
    invoiceContactPerson: invoiceContactPerson,
    userIdsToBeCreated: [],
    userIdsToBeUpdated: [],
  };

  const methods = useForm<CustomerOrganizationForm>({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    // reset async defaultValues on first render
    if (
      customerOrganizationDetails &&
      !isLoading &&
      !methods.formState.isSubmitting
    ) {
      methods.reset({
        name: customerOrganizationDetails?.name || "",
        customerNumber: customerOrganizationDetails?.customerNumber || "",
        street: customerOrganizationDetails?.billingAddress?.street || "",
        city: customerOrganizationDetails?.billingAddress?.city || "",
        zipCodeAndCity: `${
          customerOrganizationDetails?.billingAddress?.zip || ""
        } ${customerOrganizationDetails?.billingAddress?.city || ""}`,
        eezyContactPerson: eezyContactPerson,
        businessId: customerOrganizationDetails?.businessId || "",
        salesPermission: {
          verificationDate:
            customerOrganizationDetails?.salesPermission?.verificationDate,
          riskLevel: customerOrganizationDetails?.salesPermission?.riskLevel,
        },
        customerCompanyId: customerOrganizationDetails?.customerCompanyId || "",
        isNationwide: customerOrganizationDetails?.isNationwide || false,
        invoiceSettings,
        invoiceReferences: invoiceReferences,
        invoiceContactPerson: invoiceContactPerson,
        customerContacts,
        industry,
        userIdsToBeCreated: [],
        userIdsToBeUpdated: [],
      });
      setIsDataReset(true);
    }
    // eslint-disable-next-line
  }, [
    customerOrganizationDetails,
    customerOrganizationDetails?.invoiceReferences,
    isLoading,
  ]);

  useEffect(() => {
    // Trigger form validation after async defaultValues are reset
    if (isDataReset) {
      methods.trigger();
      setIsDataReset(false);
    }
  }, [isDataReset, methods]);

  const onSave = async (data: FieldValues) => {
    if (customerOrgId) {
      const newIdsForUsers: string[] = [];
      const promises: any[] = [];

      // Create users
      data.userIdsToBeCreated.forEach((userId: string) => {
        const user = data.customerContacts.find(
          (contact: CustomerUser) => contact.id === userId,
        );
        if (!user) return;
        const params = {
          ...user.account,
          customerCompanyId,
        };
        promises.push(dispatch(createNewCustomerUser(params)));
      });

      // Update users
      data.userIdsToBeUpdated.forEach((userId: string) => {
        const user = data.customerContacts.find(
          (contact: CustomerUser) => contact.id === userId,
        );
        if (!user) return;
        const params = {
          customerUserId: user.id,
          ...user,
          ...user.account,
        };
        promises.push(dispatch(updateCustomerAppUser(params)));
      });

      let customerUserOperationError = false;

      await Promise.all<Response[]>(promises).then((responses) => {
        // Go through the responses and get the ids of the created users
        responses.forEach((action) => {
          if (isFulfilled(action)) {
            // Check if we created a new customer user and add the id to the array
            if (Array.isArray(action.payload)) {
              const customerUserCreated = action.payload.find(
                (event) => event.name === "custUsrCreated",
              );
              if (customerUserCreated) {
                newIdsForUsers.push(customerUserCreated.id);
              }
            }
          }
          if (isRejected(action)) {
            // Error happened
            customerUserOperationError = true;
            return;
          }
        });
      });

      // Don't continue if there was an error
      if (customerUserOperationError) return;

      // Get all customer user ids for the company
      const allCustomerUserIdsForCompany = data.customerContacts.map(
        (customerContact: CustomerUser) => customerContact.id,
      );

      // Remove userIdsToBeCreated, these were temporary ids
      const updatedUserIds = allCustomerUserIdsForCompany.filter(
        (userId: string) => !data.userIdsToBeCreated.includes(userId),
      );

      // Add newIdsForUsers, these are the real ids that were created
      const updatedUserIdsWithNewIds = updatedUserIds.concat(newIdsForUsers);

      // Format customerContacts
      const formattedCustomerContacts = updatedUserIdsWithNewIds.map(
        (userId: string) => ({ id: userId }),
      );

      const invoiceDelivery: Record<DeliveryMethod, InvoiceDelivery> = {
        [DeliveryMethod.BY_EMAIL]: {
          method: DeliveryMethod.BY_EMAIL,
          address: data.invoiceSettings.emailAddress,
        },
        [DeliveryMethod.BY_OPERATOR]: {
          method: DeliveryMethod.BY_OPERATOR,
        },
        [DeliveryMethod.BY_E_INVOICE]: {
          method: DeliveryMethod.BY_E_INVOICE,
          address: data.invoiceSettings.address,
          operatorId: data.invoiceSettings.operatorId,
        },
      };

      const updatedDeliveryMethod: DeliveryMethod =
        data.invoiceSettings.deliveryMethod;

      // update company classification if 'isNationwide' checkbox value was changed
      if (data.isNationwide !== defaultValues.isNationwide) {
        dispatch(
          updateCustomerCompanyClassification({
            companyId: data.customerCompanyId,
            isNationwide: data.isNationwide,
          }),
        );
      }

      const organization = {
        id: customerOrgId,
        salesPersonId:
          data.eezyContactPerson?.salesPersonId ||
          customerOrganizationDetails.salesPersonId,
        salesPersonName:
          data.eezyContactPerson?.salesPersonName ||
          customerOrganizationDetails.salesPersonName,
        customerContacts: formattedCustomerContacts,
        invoiceReferences: data.invoiceReferences,
        invoiceContactPerson: data.invoiceContactPerson
          ? {
              id: data.invoiceContactPerson.id,
            }
          : null,
        invoiceDelivery: invoiceDelivery[updatedDeliveryMethod],
        version: customerOrganizationDetails.version,
      };
      dispatch(updateCustomerOrg(organization)).then((response) => {
        if (isFulfilled(response)) {
          sendMessage(t("common:SAVING_SUCCESS"), "success");
        }
      });
    }
  };

  const showOverlaySpinner = isLoading || methods.formState.isSubmitting;
  const isFormValid = methods.formState.isValid;

  const saveButtonDisabled =
    !methods.formState.isDirty ||
    !isEmpty(methods.formState.errors) ||
    !isFormValid ||
    showOverlaySpinner;

  return (
    <FormProvider {...methods}>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <FormContainer maxWidth="915px" minHeight="350px">
          <OverlaySpinner isLoading={showOverlaySpinner} />
          <CompanyBasicInfo
            companyClassificationIsEnabled={
              isAdmin && customerOrganizationDetails.isRepresentative
            }
          />
          <EezyContactPerson />
          <InvoiceInfo />
          {customerCompanyId && (
            <CustomerContacts customerCompanyId={customerCompanyId} />
          )}
          <InvoiceReferencesSection
            tooltipTitle={t("customers:companyInfo.invoiceReferencesTooltip")}
          />
          <ActionButtonsContainer>
            <CustomButton
              accessRightChecks={[canExpertEditCustomer]}
              onClick={methods.handleSubmit(onSave)}
              sx={{
                borderRadius: "18px",
                height: "36px",
              }}
              disabled={saveButtonDisabled}
              variant="contained"
            >
              {t("common:save")}
            </CustomButton>
          </ActionButtonsContainer>
        </FormContainer>
      </UnsavedChangesProvider>
    </FormProvider>
  );
};

export default CompanyInfoTabContent;
